/**
 * Client du relais distant.
 *
 * Enveloppe minimale de l'API décrite dans `server/README.md`. Aucune logique
 * électorale ici : on transporte des événements et des bulletins, on lit un
 * état public. Les règles du scrutin restent dans `src/core`.
 *
 * L'URL de base est surchargeable pour les essais : elle est mémorisée sous la
 * clé `election.nsi.xyz/v1/api`.
 */

import { CODE_LENGTHS, isValidCode, type JournalEvent, type Phase } from '../core/index.js';

export const API_PAR_DEFAUT = 'https://temps-reel-election.nsi.xyz/api';
const CLE_API = 'election.nsi.xyz/v1/api';

export function baseApi(): string {
  try {
    return localStorage.getItem(CLE_API) ?? API_PAR_DEFAUT;
  } catch {
    return API_PAR_DEFAUT;
  }
}

export function definirBaseApi(base: string | null): void {
  if (base === null) localStorage.removeItem(CLE_API);
  else localStorage.setItem(CLE_API, base);
}

/** Item tel que publié par l'enseignant, sans les empreintes de jetons. */
export interface ItemPublic {
  readonly id: string;
  readonly label: string;
  readonly kind: 'binome' | 'individu';
}

/** État public d'une élection : ce que le serveur accepte de rendre à tous. */
export interface EtatPublic {
  readonly phase: Phase;
  readonly salt: string;
  readonly items: readonly ItemPublic[];
  readonly voixDisponibles: number;
  readonly inscrits: number;
  readonly votants: number;
  readonly blancs: number;
  readonly nuls: number;
  readonly nom?: string;
  readonly classe?: string;
}

export class ErreurRelais extends Error {
  constructor(
    message: string,
    readonly statut: number,
  ) {
    super(message);
    this.name = 'ErreurRelais';
  }
}

async function appeler(chemin: string, options: RequestInit = {}): Promise<Response> {
  const reponse = await fetch(`${baseApi()}${chemin}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
  });
  if (reponse.status >= 500) {
    throw new ErreurRelais('le relais est indisponible', reponse.status);
  }
  return reponse;
}

async function lireErreur(reponse: Response): Promise<string> {
  try {
    const corps = (await reponse.json()) as { erreur?: string };
    return corps.erreur ?? `erreur ${reponse.status}`;
  } catch {
    return `erreur ${reponse.status}`;
  }
}

/* ------------------------------------------------------------------ lecture */

export interface ReponseEtat {
  readonly version: number;
  readonly etat: EtatPublic | null;
  readonly code: string | null;
  readonly etiquette: string | null;
  /** Vrai si le serveur a répondu 304 : rien n'a changé depuis l'étiquette. */
  readonly inchange: boolean;
}

export async function lireEtat(electionId: string, etiquette: string | null = null): Promise<ReponseEtat> {
  const reponse = await appeler(`/elections/${encodeURIComponent(electionId)}/state`, {
    headers: etiquette === null ? {} : { 'If-None-Match': etiquette },
  });
  if (reponse.status === 304) {
    return { version: 0, etat: null, code: null, etiquette, inchange: true };
  }
  if (!reponse.ok) throw new ErreurRelais(await lireErreur(reponse), reponse.status);
  const corps = (await reponse.json()) as { version: number; etat: EtatPublic | null; code: string | null };
  return { version: corps.version, etat: corps.etat, code: corps.code, etiquette: reponse.headers.get('etag'), inchange: false };
}

/**
 * Code de liaison : six symboles, projetés sous la forme `ABC-123`.
 *
 * Il ne doit pas être confondu avec les codes personnels de la planche
 * imprimée (neuf ou treize caractères). Cette confusion est l'erreur la plus
 * fréquente au premier vote : on la détecte donc **avant** d'interroger le
 * relais, pour répondre précisément au lieu de « code inconnu ».
 */
export type VerdictCodeLiaison =
  | { readonly ok: true; readonly code: string }
  | { readonly ok: false; readonly motif: 'personnel' | 'enseignant' | 'forme' };

export function verifierCodeLiaison(saisie: string): VerdictCodeLiaison {
  const symboles = saisie
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');
  if (symboles.length === 6) {
    return { ok: true, code: `${symboles.slice(0, 3)}-${symboles.slice(3)}` };
  }
  if (isValidCode(symboles, CODE_LENGTHS.eleve)) return { ok: false, motif: 'personnel' };
  if (isValidCode(symboles, CODE_LENGTHS.enseignant)) return { ok: false, motif: 'enseignant' };
  return { ok: false, motif: 'forme' };
}

/** Résout un code de liaison court en identifiant d'élection. */
export async function resoudreCode(code: string): Promise<string> {
  const verdict = verifierCodeLiaison(code);
  const propre = verdict.ok ? verdict.code : code.trim().toUpperCase().replace(/[^0-9A-Z-]/g, '');
  const reponse = await appeler(`/join/${encodeURIComponent(propre)}`);
  if (reponse.status === 404) throw new ErreurRelais('code de liaison inconnu', 404);
  if (!reponse.ok) throw new ErreurRelais(await lireErreur(reponse), reponse.status);
  const corps = (await reponse.json()) as { electionId: string };
  return corps.electionId;
}

/* --------------------------------------------------------------- présence */

/**
 * Combien d'appareils ont ouvert la page de vote récemment.
 *
 * Le relais ne garde qu'un identifiant opaque et un horodatage, en mémoire :
 * aucune identité, aucune trace sur disque, rien dans le journal. Un relais
 * plus ancien ne connaît pas la route : on le note une fois pour toutes, et
 * l'interface masque l'indicateur au lieu de répéter une requête vouée à 404.
 */
let presenceIndisponible = false;

export async function lirePresence(electionId: string): Promise<number | null> {
  if (presenceIndisponible) return null;
  try {
    const reponse = await appeler(`/elections/${encodeURIComponent(electionId)}/presence`);
    if (reponse.status === 404) {
      presenceIndisponible = true;
      return null;
    }
    if (!reponse.ok) return null;
    const corps = (await reponse.json()) as { connectes?: number };
    return typeof corps.connectes === 'number' ? corps.connectes : null;
  } catch {
    return null;
  }
}

/** Signale qu'un appareil est sur la page de vote. Ne bloque jamais le vote. */
export async function signalerPresence(electionId: string, clientId: string): Promise<void> {
  if (presenceIndisponible) return;
  try {
    const reponse = await appeler(`/elections/${encodeURIComponent(electionId)}/presence`, {
      method: 'POST',
      body: JSON.stringify({ clientId }),
    });
    if (reponse.status === 404) presenceIndisponible = true;
  } catch {
    // La présence est un confort : son échec ne doit jamais gêner un vote.
  }
}

/* ---------------------------------------------------------------- écritures */

export interface BulletinDistant {
  readonly tokenHash: string;
  readonly choices: readonly string[];
  readonly blank: boolean;
  readonly round: 1 | 2;
}

export type ResultatBulletin = 'ok' | 'double' | 'inconnu' | 'ferme' | 'occupe';

export async function deposerBulletin(electionId: string, bulletin: BulletinDistant): Promise<ResultatBulletin> {
  const reponse = await appeler(`/elections/${encodeURIComponent(electionId)}/ballot`, {
    method: 'POST',
    body: JSON.stringify(bulletin),
  });
  if (reponse.status === 201) return 'ok';
  if (reponse.status === 429) return 'occupe';
  const message = await lireErreur(reponse);
  if (reponse.status === 403) return 'inconnu';
  if (reponse.status === 409) return message.includes('déjà voté') ? 'double' : 'ferme';
  throw new ErreurRelais(message, reponse.status);
}

export interface ParametresCreation {  readonly electionId: string;
  readonly teacherHash: string;
  readonly journal: readonly JournalEvent[];
  readonly meta: { nom: string; classe: string; etablissement: string; expiresAt?: number };
}

/** Publie l'élection sur le relais et récupère son code de liaison. */
export async function creerElectionDistant(parametres: ParametresCreation): Promise<string> {
  const reponse = await appeler('/elections', { method: 'POST', body: JSON.stringify(parametres) });
  if (!reponse.ok) throw new ErreurRelais(await lireErreur(reponse), reponse.status);
  const corps = (await reponse.json()) as { code: string };
  return corps.code;
}

/** Journal complet, réservé à l'enseignant. */
export async function lireJournal(electionId: string, secret: string): Promise<JournalEvent[]> {
  const reponse = await appeler(`/elections/${encodeURIComponent(electionId)}/journal`, {
    headers: { Authorization: `Bearer ${secret}` },
  });
  if (reponse.status === 401) throw new ErreurRelais('secret enseignant refusé', 401);
  if (!reponse.ok) throw new ErreurRelais(await lireErreur(reponse), reponse.status);
  const corps = (await reponse.json()) as { evenements: JournalEvent[] };
  return corps.evenements;
}

/** Ajoute des événements. Le serveur les déduplique par identifiant. */
export async function ajouterEvenements(
  electionId: string,
  secret: string,
  evenements: readonly JournalEvent[],
): Promise<number> {
  const reponse = await appeler(`/elections/${encodeURIComponent(electionId)}/events`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${secret}` },
    body: JSON.stringify({ evenements }),
  });
  if (!reponse.ok) throw new ErreurRelais(await lireErreur(reponse), reponse.status);
  const corps = (await reponse.json()) as { ajoute: number };
  return corps.ajoute;
}
